class BundlePickerComponent extends HTMLElement {
  /** @type {{ variantId: string, quantity: number } | null} */
  #selection = null;

  connectedCallback() {
    const config = window.BundleConfig;
    if (!config) {
      console.error('[bundle-picker] window.BundleConfig is not defined. Make sure bundle-config.js is loaded first.');
      return;
    }

    const variantIds = JSON.parse(this.dataset.variants || '[]');

    this.#render(this.#applyHiddenOptions(config), variantIds);
    this.#bindEvents();
    this.#watchSubmit();
  }

  /**
   * Blendet die per `data-hidden-options` gelisteten Optionen aus (z. B. die
   * 1-Packung-Option auf den Landingpages). Der globale Config bleibt unberuehrt.
   * @param {typeof window.BundleConfig} config
   * @returns {typeof window.BundleConfig}
   */
  #applyHiddenOptions(config) {
    const hidden = (this.dataset.hiddenOptions || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);

    if (hidden.length === 0) return config;

    const options = config.options.filter((option) => !hidden.includes(option.id));

    return { ...config, options: options.length > 0 ? options : config.options };
  }

  /**
   * @param {typeof window.BundleConfig} config
   * @param {number[]} variantIds
   */
  #render(config, variantIds) {
    const dividerText = this.closest('.bundle-picker-block, .bundle-picker-section')?.querySelector('.bundle-picker__divider-text');
    const optionsContainer = this.querySelector('.bundle-picker__options');

    if (!optionsContainer) return;

    if (dividerText) dividerText.textContent = config.sectionTitle;

    const defaultOption = config.options.find((o) => o.recommended) ?? config.options[0];
    if (defaultOption) {
      const defaultVariantId = String(variantIds[defaultOption.variantIndex] ?? variantIds[0] ?? '');
      this.#updateForm(defaultVariantId, defaultOption.quantity ?? 1);
    }

    optionsContainer.innerHTML = config.options
      .map((option) => {
        // Fall back to first variant if the configured index doesn't exist
        const variantId = variantIds[option.variantIndex] ?? variantIds[0] ?? '';
        const isSelected = option === defaultOption;

        const badgeHtml = option.badge
          ? `<span class="bundle-option__badge bundle-option__badge--deal">${option.badge}</span>`
          : '';

        const recommendedHtml = option.recommended
          ? `<span class="bundle-option__badge bundle-option__badge--recommended">${config.recommendedLabel}</span>`
          : '';

        const originalPriceHtml = option.originalPrice
          ? `<span class="bundle-option__original-price">${option.originalPrice}</span>`
          : '';

        const noteHtml = option.note
          ? `<span class="bundle-option__note">${option.note}</span>`
          : '';

        return `
          <label
            class="bundle-option${isSelected ? ' bundle-option--selected' : ''}"
            data-variant-id="${variantId}"
            data-quantity="${option.quantity ?? 1}"
            role="radio"
            aria-checked="${isSelected}"
            tabindex="${isSelected ? '0' : '-1'}"
          >
            ${recommendedHtml}
            ${badgeHtml}
            <span class="bundle-option__radio">
              <span class="bundle-option__radio-dot"></span>
            </span>
            <span class="bundle-option__label">${option.label}</span>
            <span class="bundle-option__pricing">
              <span class="bundle-option__price">${option.price}</span>
              ${originalPriceHtml}
              ${noteHtml}
            </span>
          </label>
        `;
      })
      .join('');
  }

  #bindEvents() {
    const optionsContainer = this.querySelector('.bundle-picker__options');

    optionsContainer?.addEventListener('click', (e) => {
      const option = /** @type {HTMLElement} */ (e.target).closest('.bundle-option');
      if (!option) return;
      this.#selectOption(option);
    });

    optionsContainer?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        const option = /** @type {HTMLElement} */ (e.target).closest('.bundle-option');
        if (!option) return;
        e.preventDefault();
        this.#selectOption(option);
      }
    });
  }

  /** @param {HTMLElement} selectedOption */
  #selectOption(selectedOption) {
    this.querySelectorAll('.bundle-option').forEach((el) => {
      el.classList.remove('bundle-option--selected');
      el.setAttribute('aria-checked', 'false');
      el.setAttribute('tabindex', '-1');
    });

    selectedOption.classList.add('bundle-option--selected');
    selectedOption.setAttribute('aria-checked', 'true');
    selectedOption.setAttribute('tabindex', '0');

    this.#updateForm(
      selectedOption.dataset.variantId ?? '',
      Number(selectedOption.dataset.quantity ?? 1)
    );
  }

  /**
   * Re-apply the selection right before the form is submitted.
   *
   * Writing the quantity only on click is not enough: Horizon re-renders and
   * morphs the buy-buttons block on cart and variant updates, and the fresh
   * server markup always carries quantity 1. The picker would still show
   * "30 Packungen" while the form had long fallen back to a single pack. The
   * listener sits in the capture phase, so it runs before
   * product-form-component reads the form data.
   */
  #watchSubmit() {
    const root = this.closest('.shopify-section') ?? document;

    root.addEventListener(
      'submit',
      (event) => {
        const form = /** @type {HTMLElement} */ (event.target);
        if (!(form instanceof HTMLFormElement)) return;
        // Only our own form — a section could hold more than one.
        if (!form.contains(/** @type {Node} */ (this.#scope.querySelector('[ref="variantId"]')))) return;

        this.#applySelection();
      },
      { capture: true }
    );
  }

  /**
   * The element the form lookup is limited to.
   *
   * Landing pages render this picker more than once, each instance next to its
   * own product form. A document-wide lookup would make every picker write into
   * the first form on the page, so we stay inside the surrounding section. If
   * that section holds no form — a placement we don't use today, but that used
   * to work — we fall back to the document.
   *
   * @returns {ParentNode}
   */
  get #scope() {
    const section = this.closest('.shopify-section');
    return section?.querySelector('[ref="variantId"]') ? section : document;
  }

  /**
   * @param {string} variantId
   * @param {number} quantity
   */
  #updateForm(variantId, quantity) {
    this.#selection = { variantId, quantity };
    this.#applySelection();
  }

  /** Writes the remembered selection into the form inside {@link #scope}. */
  #applySelection() {
    if (!this.#selection) return;

    const { variantId, quantity } = this.#selection;
    const scope = this.#scope;

    const variantInput = /** @type {HTMLInputElement | null} */ (scope.querySelector('[ref="variantId"]'));
    if (variantInput) {
      variantInput.value = variantId;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Update quantity — try the component's public API first, then fall back to the raw input
    const quantitySelector = /** @type {any} */ (scope.querySelector('quantity-selector-component'));
    if (quantitySelector?.setValue) {
      quantitySelector.setValue(quantity);
    } else {
      const quantityInput = /** @type {HTMLInputElement | null} */ (
        quantitySelector?.querySelector('input[ref="quantityInput"]') ??
        scope.querySelector('input[name="quantity"]')
      );
      if (quantityInput) {
        quantityInput.value = String(quantity);
      }
    }
  }
}

if (!customElements.get('bundle-picker-component')) {
  customElements.define('bundle-picker-component', BundlePickerComponent);
}
